import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link, useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Plus,
  User,
  Phone,
  Envelope,
  Tag,
  X,
  Archive,
  Eye,
  EyeSlash,
  SpinnerGap,
} from "phosphor-react";
import CustomerCreateModal from "../components/CustomerCreateModal";
import BottomQuickActions from "../components/BottomQuickActions";
import ImportCustomersCard from "../components/ImportCustomersCard";
import { useSearchFilter } from "../layouts/AppLayout";
import { OnboardingGuidanceBanner } from "../components/FirstValueOnboardingOverlay";
import RealFirstJobGuidedFlow from "../components/RealFirstJobGuidedFlow";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [availableTags, setAvailableTags] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedCount, setArchivedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showOnboardingGuidance, setShowOnboardingGuidance] = useState(false);
  const [showRealFirstJobFlow, setShowRealFirstJobFlow] = useState(false);
  
  // URL params for auto-opening create modal
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Track current offset for pagination
  const offsetRef = useRef(0);
  const LIMIT = 100;
  
  // Track if initial mount is complete (to avoid double fetch)
  const isInitialMountRef = useRef(true);

  // Use shared filter from layout header
  const { customerFilter: filterQuery } = useSearchFilter();
  
  // Debounce timer ref
  const debounceTimerRef = useRef(null);
  
  // Handle ?new=1 query param to auto-open create modal
  // Handle ?onboarding=true to show legacy guidance banner
  // Handle ?onboarding=real_first_job to launch the guided real-first-job flow
  useEffect(() => {
    const shouldOpenNew = searchParams.get("new") === "1";
    const onboardingParam = searchParams.get("onboarding");
    const isRealFirstJobOnboarding = onboardingParam === "real_first_job";
    const isLegacyOnboardingBanner = onboardingParam === "true";

    if (isRealFirstJobOnboarding) {
      // Guided multi-step flow: customer -> job -> schedule
      setShowRealFirstJobFlow(true);
      setShowOnboardingGuidance(true);
      // Clean the URL so refresh / back navigation doesn't reopen the flow
      navigate(location.pathname, { replace: true });
      return;
    }

    if (isLegacyOnboardingBanner) {
      setShowOnboardingGuidance(true);
    }

    if (shouldOpenNew && !isCreateModalOpen) {
      setIsCreateModalOpen(true);
      navigate(location.pathname, { replace: true });
    }
  }, [searchParams, isCreateModalOpen, navigate, location.pathname]);

  // Fetch available tags
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await fetch(`${API}/tags/recent?limit=30`, { credentials: "include" });
        if (response.ok) {
          const data = await response.json();
          setAvailableTags(data.tags || []);
        }
      } catch (error) {
        console.error("Error fetching tags:", error);
      }
    };
    fetchTags();
  }, []);

  // Server-side search function
  const fetchCustomers = useCallback(async (searchTerm = "", offset = 0, append = false) => {
    try {
      // Only show full loading state on initial load (no existing customers)
      // This prevents "list -> loading -> list" flash on refetch
      if (offset === 0 && customers.length === 0) {
        setLoading(true);
      } else if (offset > 0) {
        setLoadingMore(true);
      }
      // Note: When refetching with existing data, we keep list visible (no setLoading)
      
      if (searchTerm && offset === 0) {
        setSearching(true);
      }
      
      const params = new URLSearchParams();
      params.append("limit", LIMIT.toString());
      params.append("skip", offset.toString());
      
      // Server-side search
      if (searchTerm && searchTerm.trim()) {
        params.append("search", searchTerm.trim());
      }
      
      // Tag filter
      if (selectedTag) {
        params.append("tag", selectedTag);
      }
      
      // Archive filter
      if (showArchived) {
        params.append("include_archived", "true");
      }
      
      const url = `${API}/customers?${params.toString()}`;
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        
        if (append) {
          setCustomers(prev => [...prev, ...data.customers]);
        } else {
          setCustomers(data.customers);
        }
        
        setTotalCount(data.total);
        setArchivedCount(data.archived_count || 0);
        setHasMore(data.has_more || false);
        offsetRef.current = offset + data.customers.length;
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setSearching(false);
    }
  }, [selectedTag, showArchived, customers.length]);

  // Debounced search effect - triggers when filterQuery changes
  // This is the PRIMARY fetch trigger on mount and search changes
  useEffect(() => {
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Reset offset when search term changes
    offsetRef.current = 0;
    
    // Debounce the search
    debounceTimerRef.current = setTimeout(() => {
      fetchCustomers(filterQuery, 0, false);
    }, 200);
    
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [filterQuery, fetchCustomers]);

  // Refetch when tag or archive filter CHANGES (not on initial mount)
  // Skip initial mount to avoid double fetch with the search effect above
  useEffect(() => {
    if (isInitialMountRef.current) {
      // Mark initial mount as complete after first render cycle
      isInitialMountRef.current = false;
      return;
    }
    
    // Only refetch when filters actually change (after initial load)
    offsetRef.current = 0;
    fetchCustomers(filterQuery, 0, false);
  }, [selectedTag, showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load more handler
  const handleLoadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchCustomers(filterQuery, offsetRef.current, true);
    }
  }, [loadingMore, hasMore, filterQuery, fetchCustomers]);

  // Sort customers alphabetically by name (A → Z)
  const displayedCustomers = useMemo(() => {
    return [...customers].sort((a, b) => 
      (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase())
    );
  }, [customers]);

  const handleCustomerCreated = (customer) => {
    setCustomers((prev) => [customer, ...prev]);
    // Refetch to get accurate counts
    fetchCustomers(filterQuery, 0, false);
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Onboarding guidance banner - shown when user starts fresh */}
      {showOnboardingGuidance && <OnboardingGuidanceBanner type="customer" />}
      
      {/* Header - Add Customer button and archive toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {/* Show archived toggle */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowArchived(!showArchived)}
            className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg border transition-colors ${
              showArchived 
                ? "border-purple-300 bg-purple-50 text-purple-700" 
                : "border-zinc-200 text-zinc-500 hover:border-zinc-300"
            }`}
            data-testid="show-archived-toggle"
          >
            {showArchived ? <Eye size={16} /> : <EyeSlash size={16} />}
            {showArchived ? "Showing archived" : "Show archived"}
            {archivedCount > 0 && !showArchived && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-zinc-200 rounded-full">
                {archivedCount}
              </span>
            )}
          </button>
        </div>
        
        <Button
          onClick={() => setIsCreateModalOpen(true)}
          className="bg-[#FF6600] hover:bg-[#CC5200] text-white font-heading uppercase text-sm"
          data-testid="add-customer-btn"
        >
          <Plus size={18} className="mr-2" />
          Add Customer
        </Button>
      </div>

      {/* Tag Filter */}
      {availableTags.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="tag-filter-section">
          <Tag size={16} className="text-zinc-400" />
          <span className="text-xs text-zinc-500 mr-1">Filter by tag:</span>
          {selectedTag && (
            <button
              onClick={() => setSelectedTag(null)}
              className="px-2 py-1 text-xs rounded-full bg-[#FF6600] text-white flex items-center gap-1"
              data-testid="clear-tag-filter"
            >
              {selectedTag}
              <X size={12} />
            </button>
          )}
          {availableTags.filter(t => t !== selectedTag).slice(0, 10).map((tag) => (
            <button
              key={tag}
              onClick={() => setSelectedTag(tag)}
              className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                selectedTag === tag
                  ? "bg-[#FF6600] text-white border-[#FF6600]"
                  : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300"
              }`}
              data-testid={`tag-filter-${tag}`}
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      {/* Customer List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 bg-zinc-200 rounded-sm animate-pulse" />
          ))}
        </div>
      ) : displayedCustomers.length > 0 ? (
        <div className="space-y-4">
          {/* Show search/filter indicator */}
          <div className="flex items-center justify-between text-sm text-zinc-500 bg-zinc-100 px-3 py-2 rounded-lg" data-testid="filter-results-count">
            <span>
              {filterQuery.trim() ? (
                <>
                  {searching && <SpinnerGap size={14} className="inline animate-spin mr-1" />}
                  Found {totalCount} customer{totalCount !== 1 ? 's' : ''} matching &quot;{filterQuery}&quot;
                </>
              ) : (
                <>Showing {displayedCustomers.length} of {totalCount} customers</>
              )}
            </span>
          </div>
          {displayedCustomers.map((customer) => {
            const isArchived = customer.is_archived || customer.is_active === false || customer.deleted_at;
            return (
            <Link key={customer.customer_id} to={`/customers/${customer.customer_id}`}>
              <Card className={`border-2 card-hover ${isArchived ? "border-purple-200 bg-purple-50/30" : "border-zinc-200"}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 border-2 flex items-center justify-center ${
                      isArchived ? "bg-purple-100 border-purple-200" : "bg-zinc-100 border-zinc-200"
                    }`}>
                      {isArchived ? (
                        <Archive size={24} className="text-purple-400" />
                      ) : (
                        <User size={24} className="text-zinc-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-heading text-base font-semibold uppercase truncate">
                          {customer.name}
                        </h3>
                        {isArchived && (
                          <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700 border-purple-200">
                            Archived
                          </Badge>
                        )}
                      </div>
                      <div className="hidden sm:flex items-center gap-6 text-sm text-zinc-500">
                        {customer.email && (
                          <span className="flex items-center gap-1">
                            <Envelope size={14} />
                            <span className="truncate max-w-[180px]">{customer.email}</span>
                          </span>
                        )}
                        {customer.phone && (
                          <span className="flex items-center gap-1">
                            <Phone size={14} />
                            <span>{customer.phone}</span>
                          </span>
                        )}
                      </div>
                      {/* Tags */}
                      {customer.tags && customer.tags.length > 0 && (
                        <div className="flex gap-1 mt-2 flex-wrap">
                          {customer.tags.slice(0, 3).map((tag) => (
                            <Badge
                              key={tag}
                              variant="secondary"
                              className="text-xs bg-zinc-100 text-zinc-600"
                            >
                              {tag}
                            </Badge>
                          ))}
                          {customer.tags.length > 3 && (
                            <Badge variant="secondary" className="text-xs bg-zinc-100 text-zinc-600">
                              +{customer.tags.length - 3}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
            );
          })}
          
          {/* Load More Button */}
          {hasMore && !filterQuery.trim() && (
            <div className="flex justify-center pt-4">
              <Button
                variant="outline"
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="border-2"
                data-testid="load-more-btn"
              >
                {loadingMore ? (
                  <>
                    <SpinnerGap size={16} className="animate-spin mr-2" />
                    Loading...
                  </>
                ) : (
                  <>Load More ({totalCount - displayedCustomers.length} remaining)</>
                )}
              </Button>
            </div>
          )}
        </div>
      ) : filterQuery.trim() ? (
        <Card className="border-2 border-dashed border-zinc-300">
          <CardContent className="p-12 text-center">
            <User size={48} className="mx-auto mb-4 text-zinc-300" />
            <h3 className="font-heading text-lg font-semibold uppercase mb-2">
              No Matching Customers
            </h3>
            <p className="text-zinc-500">
              No customers found for &quot;{filterQuery}&quot;
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-2 border-dashed border-zinc-300">
          <CardContent className="p-12 text-center">
            <User size={48} className="mx-auto mb-4 text-zinc-300" />
            <h3 className="font-heading text-lg font-semibold uppercase mb-2">
              No Customers Yet
            </h3>
            <p className="text-zinc-500 mb-6">
              Add your first customer to get started
            </p>
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              className="bg-[#FF6600] hover:bg-[#CC5200] text-white font-heading uppercase text-sm"
            >
              <Plus size={18} className="mr-2" />
              Add Customer
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Canonical Customer Create Modal */}
      <CustomerCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCustomerCreated={handleCustomerCreated}
      />

      {/* First-Value Onboarding: guided real-first-job flow (customer -> job -> schedule) */}
      <RealFirstJobGuidedFlow
        isOpen={showRealFirstJobFlow}
        onClose={() => {
          setShowRealFirstJobFlow(false);
          setShowOnboardingGuidance(false);
        }}
        onComplete={({ customerId, jobId, visitId, action }) => {
          setShowRealFirstJobFlow(false);
          setShowOnboardingGuidance(false);
          // Refresh customer list so the new one appears
          fetchCustomers(filterQuery, 0, false);

          if (action === 'add_another') {
            // Take the user straight to a fresh job-create flow. They'll pick
            // their just-created customer (or any existing one).
            navigate('/jobs?new=1');
            return;
          }

          // Default: View My Calendar — land them on the calendar with their
          // brand-new real visit highlighted.
          if (visitId) {
            toast.success("Your first real visit is on the calendar.");
            navigate(`/calendar?highlight_visit=${visitId}`);
          } else if (jobId) {
            navigate(`/jobs/${jobId}`);
          } else {
            navigate('/calendar');
          }
        }}
      />

      {/* Import Customers Card - at the very bottom */}
      <ImportCustomersCard onImportComplete={fetchCustomers} />

      {/* Quick Actions */}
      <BottomQuickActions />
    </div>
  );
}
